import { Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs/local-storage-key.config';
import { MENU_NAME, ROUTE_PATH } from '@core/constants';
import { buildTokenizer, convertToHiragana } from '@core/helper';
import { AuthService } from '@core/services/apis/auth.service';
import { GlobalDataService, SessionStorageService, ViewportHeightService } from '@core/services/common';
import { DpadControlService } from '@core/services/common/dpad-control.service';
import { FirebaseDeviceInfoService } from '@core/services/common/firebase/firebase-device-info.service';
import { FirebaseDatabaseService } from '@core/services/common/firebase/firebase-database.service';
import { WakeLockService } from '@core/services/common/wake-lock.service';
import { filter, Subscription, take } from 'rxjs';
import { SaucerLogService } from '@core/services/saucer-logs/saucer-log.service';

// Login screens size themselves with 100dvh and don't need the visualViewport
// correction below (built for the app shell's fixed-height scroll areas).
// Everything else stays on: ViewportHeightService now tells a keyboard-driven
// visualViewport shrink (innerHeight unchanged) apart from a real chrome
// resize (innerHeight changes too — toolbar collapse, tab-switch, rotation),
// and only re-applies --app-height for the latter (CKWEB-2662).
const VIEWPORT_FIT_SKIP_ROUTES = [
  ROUTE_PATH.CUST_LOGIN,
  ROUTE_PATH.LOGIN,
  ROUTE_PATH.USER_DASHBOARD,
  ROUTE_PATH.TV_USER_DASHBOARD,
  ROUTE_PATH.TV_USER_DASHBOARD_DETAIL,
  ROUTE_PATH.CHECK_SHEET,
  ROUTE_PATH.CHECK_SHEET_NYUSHO,
  ROUTE_PATH.CHECK_SHEET_TSUSHO,
  ROUTE_PATH.CHECK_SHEET_SHORT_TIME,
  ROUTE_PATH.CHECK_SHEET_FUNCTIONAL_TRAINING,
  ROUTE_PATH.TV_CHECK_SHEET,
  ROUTE_PATH.TV_CHECK_SHEET_NYUSHO,
  ROUTE_PATH.TV_CHECK_SHEET_TSUSHO,
  ROUTE_PATH.TV_CHECK_SHEET_SHORT_TIME,
  ROUTE_PATH.TV_CHECK_SHEET_FUNCTIONAL_TRAINING,
  ROUTE_PATH.EXCRETION_MONITOR,
  ROUTE_PATH.TV_EXCRETION_MONITOR,
  ROUTE_PATH.BATHING_SCHEDULE,
  ROUTE_PATH.TV_BATHING_SCHEDULE,
  ROUTE_PATH.BATHE_MANAGEMENT,
];
const TV_WEBVIEW_CLASS = 'tv-webview';

@Component({
  selector: 'caremoni-app',
  template: `
    <router-outlet></router-outlet>
    <caremoni-loading></caremoni-loading>
    <div class="network-disconnected-toast" *ngIf="isNetworkDisconnected && !toastDismissed">
      <span class="network-disconnected-toast__msg">ネットワークが接続できません。</span>
      <button class="network-disconnected-toast__close" (click)="dismissNetworkToast()">✕</button>
    </div>
  `,
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  wakeLock :any= null;
  isNetworkDisconnected = false;
  toastDismissed = false;
  private networkSub?: Subscription;

  constructor(
    private titleService: Title,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private globalDataService: GlobalDataService,
    private authService: AuthService,
    private firebaseDatabaseService: FirebaseDatabaseService,
    private dpadControlService: DpadControlService,
    private wakeLockService: WakeLockService,
    private viewportHeightService: ViewportHeightService,
    private firebaseDeviceInfoService: FirebaseDeviceInfoService,
    private ngZone: NgZone,
    private saucerLogService: SaucerLogService

  ) {
    this.saucerLogService.runSaucerLog();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
    .subscribe((event: any) => {
      this.setTitle(event.url)
    });
    // Remove the index.html loading logo once the first screen is shown (guard redirects end in a later NavigationEnd).
    this.router.events.pipe(filter(event => event instanceof NavigationEnd || event instanceof NavigationError), take(1))
      .subscribe(() => document.getElementById('boot-loading')?.remove());
  }
  
  async ngOnInit(): Promise<void> {
    // TV-only global styles (hide caret / focus ring) hook on this class
    if (this.firebaseDeviceInfoService.isTvWebView()) document.body.classList.add(TV_WEBVIEW_CLASS);
    this.viewportHeightService.enable(() => VIEWPORT_FIT_SKIP_ROUTES.some(path => this.router.url.includes(path)));
    await buildTokenizer();
    (window as any).receiveDataFromAndroid = (data: string) => {
      this.ngZone.run(() => this.dpadControlService.onkeyEvent(data));
    };

    await this.wakeLockService.requestWakeLock();

    this.networkSub = this.firebaseDatabaseService.isFirebaseDisconnected$.subscribe(disconnected => {
      this.isNetworkDisconnected = disconnected;
      if (!disconnected) {
        // Reconnected → reset dismissed flag so popup shows again on next disconnect
        this.toastDismissed = false;
      }
    });
  }

  dismissNetworkToast(): void {
    this.toastDismissed = true;
  }

  ngOnDestroy(): void {
    this.networkSub?.unsubscribe();
    this.viewportHeightService.disable();
  }

  setTitle(url: string) {
    if(url?.includes(ROUTE_PATH.HOME))  this.titleService.setTitle(MENU_NAME.HOME)
  }

}
